import { createSelector } from "@reduxjs/toolkit";
import getSymbolFromCurrency from "currency-symbol-map";
import { shallowEqual } from "react-redux";
import { generatePath } from "react-router";
import { Sections } from "@nxplatform/platform-entities/sport/common-sport.entities";
import { __ } from "@service/api-client/http/helpers/translates";
import { CasinoGamesView } from "./entities/common-entities.js";
import { DEFAULT_SITE_LANG, GAME_NAME, getBaseLocale } from "./helpers-pure.js";
import { lazyWithRetry } from "./retry/lazy-with-retry.js";
const isEmpty = (obj) => Array.isArray(obj) ? obj.length === 0 : Object.keys(obj).length === 0;
const selectPrettyBaseLanguage = createSelector(
  (language) => language || "",
  (language) => (language.split("_")[0] || "").toLowerCase() || DEFAULT_SITE_LANG.toLowerCase()
);
const selectPrettyBaseLocale = createSelector(
  (language) => language || "",
  (language) => (language.split("_")[1] || "").toLowerCase() || ""
);
const bindingContractsKey = "temporarilyDisableBindingContracts";
const sportFilterPeriodId = "sportFilterPeriodId";
const generateSeparatedString = (...args) => (exclude, separator) => args.map((string, idx) => string.indexOf(exclude) === -1 && idx > 0 ? `${exclude}${__(string)}` : `${__(string)}`).join(separator);
const appLanguage = localStorage.getItem("_lng") !== null ? localStorage.getItem("_lng") : "ru";
const windowObject = window;
const domainName = (env) => env.domainName || document.domain;
const imageFormats = [
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
  "gif",
  "bmp",
  "webp",
  "raw"
];
const svgSportPath = "/assets/sport/";
const imageHolderSizePath = "/assets/casino/holders";
const onPageItems = 10;
const AffiliateSetup = {
  cookieName: "btag",
  cookieNameCTag: "ctag",
  clickId: "click_id",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmMedium: "utm_medium",
  utmSource: "utm_source",
  utmTerm: "utm_term",
  subId: "subid",
  affisePid: "affise_pid",
  affiseOffer: "affise_offer",
  affiseClickId: "affise_click_id",
  partnerId: "partner_id",
  subId10: "sub_id10",
  gclid: "gclid",
  fbclid: "fbclid",
  msclkid: "msclkid"
};
const sortedByNames = (a, b) => {
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  } else {
    return 0;
  }
};
const sortSportsAlphabetic = createSelector(
  (entities) => entities,
  (entities) => entities.slice().sort((a, b) => sortedByNames(a.name, b.name))
);
const selectExistedPopularSports = createSelector(
  (sportList) => sportList || [],
  (sportList) => sportList.filter((sport) => sport.isPopular)
);
const defineSportSection = (league) => {
  return league.hasLiveEvents ? Sections.live : Sections.line;
};
const sportLeagueImageFallback = "/assets/country/ic_cup.svg";
const promotionBettorsLink = "/promotions/bettors-tournament";
const windowScrollPolyfill = ({
  element,
  x,
  y,
  behaviour
}) => {
  try {
    const scrollData = { top: Number(x), left: Number(y) };
    if (behaviour) {
      Object.assign(scrollData, { behavior: "smooth" });
    }
    element.scrollTo(scrollData);
  } catch (err) {
    if (err instanceof TypeError) {
      element.scrollTo(x, y);
    }
  }
};
const BodyLoadingClass = "animation-start";
const BodyAppReadyClass = "appReady";
const cmsContentWrapperId = "text-page-content";
const WebViewParam = "webView";
const LocationSmartWebViewHeader = "LSDeviceWebViewHeader";
const WebNavigateParam = "webNavigate";
const casinoBannerLocale = ["in", "hi", "br"];
const casinoBannerCurrentLocale = (country, language) => {
  if (!country) return "";
  if (casinoBannerLocale.includes(country.toLowerCase())) {
    return country.toLowerCase();
  }
  if (casinoBannerLocale.includes(language)) {
    if (language === "br") return "";
    return language;
  }
  return "";
};
function lazyImport(factory, name) {
  return /* @__PURE__ */ Object.create({
    [name]: lazyWithRetry(() => factory().then((module) => ({ default: module[name] })))
  });
}
const middlewareImporter = (factory, name) => () => factory().then(({ [name]: middleware }) => middleware);
const getCurrencySymbol = (code, currencyByCode) => {
  const selectedCurrency = currencyByCode(code);
  return (selectedCurrency == null ? void 0 : selectedCurrency.icon) || getSymbolFromCurrency(code);
};
const splitNumberByThousands = (value, separator) => {
  const splitByInt = `${value}`.split(/[,|.]/);
  return splitByInt[0].split(/(?=(?:...)*$)/).join(separator) + (splitByInt.length > 1 ? `.${splitByInt[1]}` : "");
};
const generateFileVersion = (isProd, hash) => {
  if (isProd && hash.length > 8) {
    return hash.slice(-8);
  }
  return "123";
};
const convertDateStringToTime = (data) => (data || []).map((season) => {
  season.time = season.startDate;
  return season;
});
const termsTranslate = "Default_._Title Or Link_._Terms and Conditions";
const policyTranslate = "Default_._Title Or Link_._Privacy Policy";
const snackbarNotificationStorageKey = "activeSnackbarNotification";
const ontarioRegistrationIdentificationKey = "identificationId";
const ApiErrorCodes = {
  selfDeletion: 16,
  selfExclusion: 3
};
const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";
const generateRandomPassword = (passwordLength = 8, randomString = "") => {
  let baseRandomString = `1_Q#_${randomString}`;
  for (let i = 0; i < passwordLength; i++) {
    const rnum = Math.floor(Math.random() * chars.length);
    baseRandomString += chars.substring(rnum, rnum + 1);
  }
  return baseRandomString;
};
const isObject = (obj) => typeof obj === "object" && !Array.isArray(obj) && obj !== null;
function mergeDeep(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return mergeDeep(target, ...sources);
}
function mapsEquality(map1, map2) {
  let testVal;
  if (!map1 || !map2) {
    return false;
  }
  if (map1.size !== map2.size) {
    return false;
  }
  for (const [key, val] of map1) {
    testVal = map2.get(key);
    if (JSON.stringify(testVal) !== JSON.stringify(val) || testVal === void 0 && !map2.has(key)) {
      return false;
    }
  }
  return true;
}
const round = (num, places) => {
  if (!`${num}`.includes("e")) {
    return +`${Math.round(Number(`${num}e+${places}`))}e-${places}`;
  }
  const arr = `${num}`.split("e");
  let sig = "";
  if (+arr[1] + places > 0) {
    sig = "+";
  }
  return +`${Math.round(
    Number(`${+arr[0]}e${sig}${+arr[1] + places}`)
  )}e-${places}`;
};
const updateOrAdd = (array = [], newArray = [], key) => {
  const copy = [...array];
  newArray.forEach((item) => {
    const index = copy.findIndex((i) => {
      if (typeof key === "string") {
        return i[key] === item[key];
      }
      if (Array.isArray(key)) {
        return key.every((k) => i[k] === item[k]);
      }
      return false;
    });
    if (index !== -1) {
      copy[index] = item;
    } else {
      copy.push(item);
    }
  });
  return copy;
};
const registrationRestrictedMessage = __(
  "Default_._Registration_._Unfortunately, registration appears to be restricted for your country."
);
const generateUniqueId = () => Math.random().toString(36).substring(2);
const generatePathWithSearchParams = (path, params, searchParams, hash) => {
  const withUrlParams = Object.keys(searchParams || {}).length ? `?${new URLSearchParams(searchParams).toString()}` : "";
  let withHash = "";
  if (hash) {
    withHash = hash.indexOf("#") > -1 ? hash : `#${hash}`;
  }
  return `${generatePath(path, params || {})}${withUrlParams}${withHash}`;
};
const typedShallowEqual = (o1, o2) => shallowEqual(o1, o2);
const COMPACT_NUMBER_DEFAULT_OPTIONS = {
  notation: "compact",
  compactDisplay: "short"
};
const getCompactNumber = (number, localeISO) => {
  const locale = (localeISO || getBaseLocale()).replace(/_/g, "-");
  const formatter = Intl.NumberFormat(locale, COMPACT_NUMBER_DEFAULT_OPTIONS);
  return formatter.format(number);
};
const getGameViewType = (gamesViewType, slider) => {
  if (gamesViewType) {
    return gamesViewType;
  }
  return slider ? CasinoGamesView.slider : CasinoGamesView.base;
};
const generateCategoryName = ({ categoryName, params }) => (params == null ? void 0 : params.gameName) && categoryName.includes(GAME_NAME) ? categoryName.replace(GAME_NAME, params.gameName) : categoryName;
const getItemId = (child) => {
  var _a;
  const id = ((_a = child.props) == null ? void 0 : _a.itemId) ?? child.key;
  return typeof id === "string" ? id.replace(/^\.\$?/, "") : String(id);
};
const notNull = (value) => value !== null && value !== void 0 && value !== "";
const isInstalledPWA = () => window.matchMedia("(display-mode: standalone)").matches;
const yearsAmount = 100;
export {
  AffiliateSetup,
  ApiErrorCodes,
  BodyAppReadyClass,
  BodyLoadingClass,
  LocationSmartWebViewHeader,
  WebNavigateParam,
  WebViewParam,
  appLanguage,
  bindingContractsKey,
  casinoBannerCurrentLocale,
  casinoBannerLocale,
  cmsContentWrapperId,
  convertDateStringToTime,
  defineSportSection,
  domainName,
  generateCategoryName,
  generateFileVersion,
  generatePathWithSearchParams,
  generateRandomPassword,
  generateSeparatedString,
  generateUniqueId,
  getCompactNumber,
  getCurrencySymbol,
  getGameViewType,
  getItemId,
  imageFormats,
  imageHolderSizePath,
  isEmpty,
  isInstalledPWA,
  isObject,
  lazyImport,
  mapsEquality,
  mergeDeep,
  middlewareImporter,
  notNull,
  onPageItems,
  ontarioRegistrationIdentificationKey,
  policyTranslate,
  promotionBettorsLink,
  registrationRestrictedMessage,
  round,
  selectExistedPopularSports,
  selectPrettyBaseLanguage,
  selectPrettyBaseLocale,
  snackbarNotificationStorageKey,
  sortSportsAlphabetic,
  sortedByNames,
  splitNumberByThousands,
  sportFilterPeriodId,
  sportLeagueImageFallback,
  svgSportPath,
  termsTranslate,
  typedShallowEqual,
  updateOrAdd,
  windowObject,
  windowScrollPolyfill,
  yearsAmount
};
